/**
 * @file CookiePolicy.js — Page statique de politique de cookies (R-0126 RGPD).
 * Explique les cookies et mécanismes de stockage local utilisés par la plateforme.
 * Précise qu'aucun cookie d'analyse ou de suivi n'est actuellement déployé.
 * Le contenu est en anglais ; l'internationalisation (i18n) sera ajoutée ultérieurement.
 */
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

/**
 * Composant de la page de politique de cookies.
 * Détaille les cookies de session essentiels (CSRF, authentification)
 * et les préférences stockées dans localStorage (thème, langue).
 * @returns {React.ReactElement}
 */
export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="cookie-policy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* ── Bouton de retour ── */}
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="mb-8 text-muted-foreground hover:text-foreground gap-1.5"
        >
          <Link to="/">
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>
        </Button>

        {/* ── En-tête ── */}
        <h1
          className="text-3xl sm:text-4xl font-medium tracking-tight text-foreground mb-2"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Cookie Policy
        </h1>
        <p className="text-sm text-muted-foreground mb-10" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          Last updated: April 2026
        </p>

        {/* ── Contenu structuré en sections ── */}
        <div className="space-y-10 text-sm leading-relaxed text-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>

          {/* Introduction */}
          <section>
            <p className="text-muted-foreground">
              This page explains how Circles (Ringo) uses cookies and similar browser storage
              mechanisms. We strive to keep cookie usage minimal and transparent.
            </p>
          </section>

          {/* Section 1 — Cookies de session essentiels */}
          <section>
            <h2 className="text-lg font-medium tracking-tight mb-3">1. Essential Session Cookies</h2>
            <p className="text-muted-foreground mb-4">
              These cookies are strictly necessary for the platform to function. They do not require
              your consent under GDPR as they are essential for the service you have requested.
            </p>
            <div className="overflow-x-auto border border-border rounded-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left px-4 py-2.5 font-medium text-foreground">Cookie</th>
                    <th className="text-left px-4 py-2.5 font-medium text-foreground">Purpose</th>
                    <th className="text-left px-4 py-2.5 font-medium text-foreground">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-border/50">
                    <td className="px-4 py-2.5"><code className="text-xs px-1 py-0.5 bg-muted rounded">csrftoken</code></td>
                    <td className="px-4 py-2.5">Protects against cross-site request forgery (CSRF) attacks.</td>
                    <td className="px-4 py-2.5">Session</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2.5"><code className="text-xs px-1 py-0.5 bg-muted rounded">sessionid</code></td>
                    <td className="px-4 py-2.5">Maintains your authenticated session after login.</td>
                    <td className="px-4 py-2.5">2 weeks</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 2 — Stockage local (localStorage) */}
          <section>
            <h2 className="text-lg font-medium tracking-tight mb-3">2. Local Storage (Not Cookies)</h2>
            <p className="text-muted-foreground mb-4">
              The following preferences are stored in your browser&apos;s <code className="text-xs px-1 py-0.5 bg-muted rounded">localStorage</code>.
              Unlike cookies, these are never sent to our servers. They stay entirely in your browser
              to remember your preferences.
            </p>
            <div className="overflow-x-auto border border-border rounded-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left px-4 py-2.5 font-medium text-foreground">Key</th>
                    <th className="text-left px-4 py-2.5 font-medium text-foreground">Purpose</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-border/50">
                    <td className="px-4 py-2.5"><code className="text-xs px-1 py-0.5 bg-muted rounded">ringo-theme</code></td>
                    <td className="px-4 py-2.5">Stores your theme preference (light or dark mode).</td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="px-4 py-2.5"><code className="text-xs px-1 py-0.5 bg-muted rounded">i18nextLng</code></td>
                    <td className="px-4 py-2.5">Stores your preferred language (English or French).</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2.5"><code className="text-xs px-1 py-0.5 bg-muted rounded">ringo-cookie-consent</code></td>
                    <td className="px-4 py-2.5">Records whether you have acknowledged the cookie banner.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 3 — Cookies d'analyse */}
          <section>
            <h2 className="text-lg font-medium tracking-tight mb-3">3. Analytics Cookies</h2>
            <p className="text-muted-foreground">
              We do <strong className="text-foreground">not</strong> currently use any analytics, tracking,
              or third-party cookies. If this changes in the future, this policy will be updated and
              you will be asked for your explicit consent before any such cookies are set.
            </p>
          </section>

          {/* Section 4 — Gestion des cookies */}
          <section>
            <h2 className="text-lg font-medium tracking-tight mb-3">4. Managing Cookies</h2>
            <p className="text-muted-foreground">
              You can control and delete cookies through your browser settings. Please note that
              disabling essential cookies will prevent you from using authenticated features of the
              platform. Clearing localStorage will reset your theme and language preferences.
            </p>
          </section>

          {/* ── Séparateur et lien vers la politique de confidentialité ── */}
          <div className="border-t border-border pt-6">
            <p className="text-muted-foreground">
              See also our{' '}
              <Link
                to="/privacy"
                className="text-foreground underline underline-offset-4 hover:text-foreground/80"
              >
                Privacy Policy
              </Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
